import React, { useState, useMemo, useEffect } from "react";
import classNames from "classnames";
import { getStickyStockTableStickyColumnCss } from "utils/reactTableTheme";
import {
    Row,
    Col,
    Button,
    Form,
    FormGroup,
    Label,
    Input,
    Spinner,
} from "reactstrap";
import ReactTable from "components/ReactTable/ReactTable";
import ReactTable2 from "components/ReactTable/ReactTable2";
import { useGetInventories } from "api/inventory/service";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Select from "components/Select";
import { getActiveColorStyle } from "utils/colorUtils";
import { useGetStatuses } from "api/inventory-status/service";
import InventoryDetail from "../../inventory/InventoryDetail";
import { formatDateForDisplay } from "utils/dateUtils";
import {
    getTheme2MultiSelectCss,
    getTheme2MultiSelectSelectConfig,
    getTheme2SearchFilterColProps,
    getTheme2SearchFilterFieldCss,
    getTheme2SearchFilterMultiSelectStyles,
    getTheme2SearchFilterStatusFieldCss,
} from "utils/theme2MultiSelect";

const SA_INVENTORY_STATUS_PREFIX = "sa-inventory-status";

const SA_INVENTORY_TAB_STYLES = (isTheme2) => `
  .sticky-stock-table .rt-table {
    min-width: 1400px;
    border-collapse: collapse;
  }
  ${isTheme2 ? `
  ${getTheme2SearchFilterFieldCss({
        pageScopeSelector: ".tenant-edit-page.tenant-edit-page--theme2 .sa-inventory-tab",
        filtersSelector: ".sa-inventory-filters",
        rowClass: "sa-inventory-filters-row",
        colClass: "sa-inventory-filter-col",
    })}
  ${getTheme2SearchFilterStatusFieldCss({
        pageScopeSelector: ".tenant-edit-page.tenant-edit-page--theme2",
        filterColClass: "sa-inventory-filter-col",
    })}
  ${getTheme2MultiSelectCss({
        prefix: SA_INVENTORY_STATUS_PREFIX,
        pageScopeSelector: ".tenant-edit-page.tenant-edit-page--theme2 .sa-inventory-tab",
    })}
  ` : ""}
  ${getStickyStockTableStickyColumnCss("")}
`;

const buildStatusOptions = (statusesData) => {
    const results = statusesData?.results || [];
    return results.map((s) => ({ label: s.status_name, value: s.status_key }));
};

const inventoryColumns = (t) => [
    { Header: t("searchInventory.stock"), accessor: "stock" },
    { Header: t("searchInventory.year"), accessor: "year", width: 60, minWidth: 60, maxWidth: 60 },
    { Header: t("searchInventory.make"), accessor: "make", width: 100, minWidth: 100, maxWidth: 100 },
    { Header: t("searchInventory.modelTrim"), accessor: "model" },
    { Header: t("searchInventory.color"), accessor: "color" },
    { Header: t("searchInventory.vin"), accessor: "vin" },
    { Header: t("searchInventory.location"), accessor: "location" },
    { Header: t("searchInventory.daysAtLocation"), accessor: "daysAtLocation" },
    { Header: t("searchInventory.acquiredDate"), accessor: "acquiredDate" },
    {
        accessor: "status",
        Header: () => (
            <div style={{ textAlign: "center", width: "100%" }}>
                {t("searchInventory.status")}
            </div>
        ),
        Cell: ({ value }) => <div style={{ textAlign: "center" }}>{value}</div>,
    },
    { Header: t("searchInventory.price"), accessor: "price" },
    { Header: t("searchInventory.mileage"), accessor: "mileage" },
    { Header: t("searchInventory.pics"), accessor: "pics" },
];

const SAInventoryTab = ({ tenantId, activeColor = "blue", isTheme2 = false }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
    const statusMultiSelectStyles = useMemo(
        () => getTheme2SearchFilterMultiSelectStyles(activeColorStyle, isTheme2),
        [activeColorStyle, isTheme2]
    );
    const statusTheme2Config = useMemo(
        () => getTheme2MultiSelectSelectConfig(SA_INVENTORY_STATUS_PREFIX, isTheme2),
        [isTheme2]
    );
    const filterColProps = getTheme2SearchFilterColProps(isTheme2, {
        filterColClass: "sa-inventory-filter-col",
        md: "4",
    });
    const statusFilterColProps = isTheme2
        ? {
            ...filterColProps,
            className: `${filterColProps.className} sa-inventory-filter-col--status`,
        }
        : filterColProps;

    const [selectId, setSelectId] = useState(null);
    const [selectedInventoryVin, setSelectedInventoryVin] = useState(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [search, setSearch] = useState("");
    const [status, setStatus] = useState([{ label: 'Available', value: 'available' }]);
    const [debouncedSearch, setDebouncedSearch] = useState("");

    useEffect(() => {
        const handler = setTimeout(() => setDebouncedSearch(search), 500);
        return () => clearTimeout(handler);
    }, [search]);

    const { data: statusesData, isLoading: isLoadingStatuses } = useGetStatuses();

    const statusOptions = useMemo(
        () => buildStatusOptions(statusesData),
        [statusesData]
    );
    const statusArray = useMemo(
        () => status.map((option) => option.value),
        [status]
    );
    const { data: inventories, isLoading } = useGetInventories(
        debouncedSearch,
        statusArray,
        undefined, // location
        undefined, // altLocation
        page,
        pageSize
    );

    const InventoryTable = isTheme2 ? ReactTable2 : ReactTable;
    const columns = useMemo(() => inventoryColumns(t), [t]);

    const inventoryData = useMemo(() => {
        return (inventories?.results || []).map((item) => ({
            id: item.id,
            documentId: item.documentId,
            stock: item.stock_number,
            year: item.inventory_vehicle?.year || "",
            make: item.inventory_vehicle?.make
                ? item.inventory_vehicle.make.charAt(0).toUpperCase() +
                item.inventory_vehicle.make.slice(1).toLowerCase()
                : "",
            model: item.inventory_vehicle?.model || "",
            color: item.inventory_vehicle?.exterior_color || "",
            vin: item?.vin || "",
            location: item.location?.legal_name || "-",
            altLocation: item.alt_location?.location_name || "-", // Eğer alt location varsa
            daysAtLocation: item.days_at_location == null ? "-" : item.days_at_location,
            acquiredDate: formatDateForDisplay(item.acquired_date),
            status: (
                <span style={{ color: item.inventory_vehicle?.vehicle_status?.color }}>
                    {item.inventory_vehicle?.vehicle_status?.status_name || "-"}
                </span>
            ),
            price: new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
            }).format(
                item.inventory_pricing?.normal_asking_price
                    ? item.inventory_pricing?.normal_asking_price
                    : 0
            ),
            mileage: item.mileage || "",
            pics: item.images_count || 0,
        }));
    }, [inventories]);




    if (selectId) {
        return (
            <div className="pt-0">
                <div className="d-flex align-items-center mb-3">
                    <Button
                        size="sm"
                        color="link"
                        className="p-0 mr-3"
                        onClick={() => {
                            setSelectId(null);
                            setSelectedInventoryVin(null);
                        }}
                        style={{ color: activeColorStyle.borderColor }}
                    >
                        <i className="tim-icons icon-minimal-left" style={{ fontSize: '1.2rem' }} />
                    </Button>
                    <h5 className="mb-0">
                        <span className="text-muted mr-1">Inventory Detail:</span>
                        {selectedInventoryVin}
                    </h5>
                </div>
                <InventoryDetail
                    inventoryId={selectId}
                    isEmbedded={true}
                    activeColor={activeColor}
                    shellThemeVariant={isTheme2 ? "2" : "1"}
                />
            </div>
        );
    }

    return (
        <div
            className={classNames("sa-inventory-tab", {
                "sa-inventory-tab--theme2": isTheme2,
            })}
        >
            <style>{SA_INVENTORY_TAB_STYLES(isTheme2)}</style>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <h5 className="mb-0">Company Inventory</h5>
                    {!isTheme2 && (
                        <span className="text-muted" style={{ fontSize: '0.85rem' }}>
                            Total Vehicles: {inventories?.pagination?.total || 0}
                        </span>
                    )}
                </div>
                <Button
                    size="sm"
                    style={{ ...activeColorStyle, color: 'white' }}
                    onClick={() => navigate("/add-inventory")}
                >
                    + Add Inventory
                </Button>
            </div>

            <div className="sa-inventory-filters">
                <Form>
                    <Row className={isTheme2 ? "sa-inventory-filters-row" : undefined}>
                        <Col {...filterColProps}>
                            <FormGroup>
                                <Label>{t('searchInventory.search')}</Label>
                                <Input
                                    type="text"
                                    placeholder={t('searchInventory.searchPlaceholder')}
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    className="form-control"
                                />
                            </FormGroup>
                        </Col>
                        <Col {...statusFilterColProps}>
                            <FormGroup>
                                <Label>{t('searchInventory.status')}:</Label>
                                <Select
                                    activeColor={activeColor}
                                    className={statusTheme2Config.controlClassName}
                                    placeholder={t('searchInventory.selectStatus')}
                                    name="inventoryStatusSelect"
                                    isSearchable
                                    closeMenuOnSelect={false}
                                    isMulti
                                    hideSelectedOptions={statusTheme2Config.hideSelectedOptions}
                                    value={status}
                                    onChange={(value) => setStatus(value || [])}
                                    options={statusOptions}
                                    isClearable
                                    isDisabled={isLoadingStatuses}
                                    styles={statusMultiSelectStyles}
                                    components={statusTheme2Config.components}
                                    classNames={statusTheme2Config.classNames}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                </Form>
            </div>

            <div
                className={isTheme2 ? "inventory-table-wrap" : undefined}
                style={isTheme2 ? { fontSize: "13px", minWidth: 0, maxWidth: "100%" } : undefined}
            >
                {isTheme2 ? (
                    <InventoryTable
                        activeColor={activeColor}
                        data={inventoryData}
                        columns={columns}
                        disableViewportClamp
                        manualPagination={true}
                        className="-striped -highlight sticky-stock-table"
                        pageCount={inventories?.pagination?.pageCount || 1}
                        pageIndex={page - 1}
                        pageSize={pageSize}
                        totalItems={inventories?.pagination?.total}
                        loading={isLoading}
                        loadingText={t("searchInventory.loadingSearchInventory")}
                        noDataText={t("searchInventory.noRecordsFound")}
                        onPageChange={(pageIndex) => setPage(pageIndex + 1)}
                        onPageSizeChange={setPageSize}
                        onRowClick={(row) => {
                            if (row?.documentId) setSelectId(row.documentId);
                            if (row?.vin) setSelectedInventoryVin(row.vin);
                        }}
                    />
                ) : isLoading ? (
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "200px" }}>
                        <Spinner color={activeColorStyle.borderColor} />
                    </div>
                ) : (
                    <InventoryTable
                        activeColor={activeColor}
                        data={inventoryData}
                        columns={columns}
                        manualPagination={true}
                        className="-striped -highlight sticky-stock-table"
                        pageCount={inventories?.pagination?.pageCount || 1}
                        pageIndex={page - 1}
                        pageSize={pageSize}
                        onPageChange={(pageIndex) => setPage(pageIndex + 1)}
                        onPageSizeChange={setPageSize}
                        onRowClick={(row) => {
                            if (row?.documentId) setSelectId(row.documentId);
                            if (row?.vin) setSelectedInventoryVin(row.vin);
                        }}
                    />
                )}
            </div>
        </div>
    );
};

export default SAInventoryTab;
